import { useMemo } from 'react';
import { Box, Text } from '@chakra-ui/react';

import {
  QuoteCarrierHeader,
  QuoteBanner,
  VehicleSummarySection,
  VehicleQuoteCard,
  RatesSection,
  MessagesSection,
  PremiumSummarySection,
  QuoteActionButtons,
} from '../components/quote';
import type { QuoteSummaryData } from 'api/quote-marketplace';
import type { QuoteLOB } from '../constants';
import { getVehiclesParsedData } from '../utils/parseVehicleHtml';

interface QuoteStepProps {
  data: QuoteSummaryData;
  quoteId: number;
  lob: QuoteLOB;
}

const QuoteStep = ({ data, quoteId, lob }: QuoteStepProps) => {
  const { selected_carrier, vehicles, effective_date } = data;

  const vehiclesWithParsedData = useMemo(() => {
    const parsedData = getVehiclesParsedData(
      vehicles,
      selected_carrier?.coverage_breakdown_html ?? null,
      selected_carrier?.discounts_html ?? null,
      selected_carrier?.rating_factors_html ?? null,
    );
    return vehicles.map((vehicle, index) => ({
      vehicle,
      parsedData: parsedData[index],
    }));
  }, [
    vehicles,
    selected_carrier?.coverage_breakdown_html,
    selected_carrier?.discounts_html,
    selected_carrier?.rating_factors_html,
  ]);

  if (!selected_carrier) {
    return (
      <Box textAlign="center" py={8}>
        <Text color="gray.500">No carrier selected</Text>
      </Box>
    );
  }

  return (
    <Box>
      <QuoteCarrierHeader
        carrierName={selected_carrier.carrier_name}
        quoteRef={selected_carrier.acturis_quote_ref}
      />

      <QuoteBanner title="Auto">
        <VehicleSummarySection
          vehicles={vehiclesWithParsedData}
          annualPremium={selected_carrier.annual_premium}
          taxes={selected_carrier.taxes}
        />

        {vehiclesWithParsedData.map(({ vehicle, parsedData }, index) => (
          <VehicleQuoteCard
            key={vehicle.id}
            vehicle={vehicle}
            parsedData={parsedData}
            vehicleIndex={index}
          />
        ))}

        <RatesSection
          effectiveDate={effective_date}
          validUntilDate={selected_carrier.valid_until_date}
          riskInfo={selected_carrier.risk_info}
        />

        <MessagesSection ratingNotes={selected_carrier.rating_notes} />

        <PremiumSummarySection
          annualPremium={selected_carrier.annual_premium}
          taxes={selected_carrier.taxes}
        />
      </QuoteBanner>

      <QuoteActionButtons quoteId={quoteId} lob={lob} />
    </Box>
  );
};

export default QuoteStep;
